import useUrl from 'core/hooks/useUrl';
import useAgencyCouriersDataStore from 'core/hooks/useAgencyCouriersDataStore';
import InformationalMessage from 'components/informationalMessage/InformationalMessage';
import { useState } from 'react';
import CourierSearch from 'components/courierSearch/CourierSearch';
import CourierTypeTabs from 'components/courierTypeTabs/CourierTypeTabs';
import AgencyMapView from 'components/agencyMapView/AgencyMapView';
import WithLoadingSpinner from 'components/withLoadingSpinner/WithLoadingSpinner';
import DesignInput from 'components/input/DesignInput';
import { Courier } from 'core/graphql/types';
import CourierCard from '../courierCard/CourierCard';
import useAgencyZonesDataStore from '../../core/hooks/useAgencyZones';

export type AgencyViewComponentProps = {
  onCourierSelected: (courierId: number) => void;
};

export enum CourierType {
  CourierWithItineraries = 1,
  CourierWithoutItineraries = 2,
  AllCouriers = 3,
}

const AgencyViewComponent = ({
  onCourierSelected,
}: AgencyViewComponentProps) => {
  const [courierType, setCourierType] = useState<CourierType>(
    CourierType.CourierWithItineraries,
  );
  const { paramValue: dateParamValue, setParamValue: setDateParamValue } =
    useUrl<string>('Date');
  const { paramValue: agencyParamValue, setParamValue: setAgencyParamValue } =
    useUrl<string>('Agency');
  const [searchQuery, setSearchQuery] = useState<string>();

  const {
    couriers: agencyCouriers,
    refresh: refetchCouriers,
    loadingCouriers: areAgencyCouriersLoading,
  } = useAgencyCouriersDataStore(dateParamValue, agencyParamValue, courierType);

  const {
    agencyZones
  } = useAgencyZonesDataStore(agencyParamValue);

  const handleDateChanged = (e: any) => {
    const { value } = e.target;
    setDateParamValue(value);
  };

  const handleAgencyChanged = ({ value }: any) => {
    setAgencyParamValue(value);
  };

  const handleTabClick = (key: any) => {
    setCourierType(key as CourierType);
  };

  const onFilterChanged = (e: any) => {
    setSearchQuery(e.target.value);
  };

  const filteredCouriers: Courier[] = agencyCouriers.filter(
    (x) =>
      (searchQuery && x.indicative?.includes(searchQuery)) ||
      (searchQuery && x.courierId.toString().includes(searchQuery)) ||
      !searchQuery,
  );

  return (
    <WithLoadingSpinner isLoading={areAgencyCouriersLoading}>
      <div className="h-full w-5/12 overflow-y-auto px-4 pt-2 pb-4">
        <CourierSearch
          dateInputValue={dateParamValue}
          dateChangeHandler={handleDateChanged}
          agencyInputValue={agencyParamValue}
          agencyChangeHandler={handleAgencyChanged}
          refreshHandler={refetchCouriers}
        />

        {!agencyParamValue || !dateParamValue ? (
          <InformationalMessage message="Please select a date and an agency first" />
        ) : (
          <div className="mb-4">
            <CourierTypeTabs
              activeTabs={courierType}
              onTabClick={handleTabClick}
            />
            <DesignInput
              value={searchQuery}
              type="text"
              name="courierSearch"
              step={undefined}
              onChanceHandler={onFilterChanged}
              placeHolder="Courier Id or Indicative"
            />
            <div className="h-full">
              {agencyCouriers.length > 0 ? (
                <ul>
                  {filteredCouriers.map((courier) => (
                    <li key={courier.courierId} className="my-4">
                      <button
                        type="button"
                        onClick={() => onCourierSelected(courier.courierId)}
                        className="w-full"
                        aria-label="Courier card"
                      >
                        <CourierCard courier={courier} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <InformationalMessage message="There are no active couriers yet" />
              )}
            </div>
          </div>
        )}
      </div>
      <div className="h-full w-7/12">
        <AgencyMapView couriers={agencyCouriers} agencyZones={agencyZones} />
      </div>
    </WithLoadingSpinner>
  );
};

export default AgencyViewComponent;
